import React from "react";
import { Link } from "react-router-dom";
import type { Product } from "../../hooks/products/types";

type BannerProps = {
  variant?: "one" | "two" | "three";
  title?: string;
  subtitle?: string;
  image?: string;
  link?: string;
  className?: string;
  products?: Product[];
  product?: Product; // Keeping for backward compatibility or single product use
};

const newLocal =
  "relative rounded-2xl overflow-hidden min-h-[250px] sm:min-h-[300px] md:min-h-[350px]";
const Banner: React.FC<BannerProps> = ({
  variant = "one",
  title,
  // subtitle,
  image,
  // link = "products",
  className = "",
  products = [],
  product: singleProduct,
}) => {
  // Helper to safely get product data
  const getProduct = (index: number) => {
    // Priority: products array -> singleProduct (if index 0)
    if (products && products.length > index) return products[index];
    if (index === 0 && singleProduct) return singleProduct;
    return undefined;
  };

  const renderEnhancedHero = () => {
    const mainProduct = getProduct(0);
    const sideProduct1 = getProduct(1);
    const sideProduct2 = getProduct(2);
    const sideProduct3 = getProduct(3);
    const sideProduct4 = getProduct(4);

    return (
      <div className="w-full py-6 sm:py-8 lg:py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
            {/* Main Banner Section - Left Side */}
            <div className="lg:col-span-7">
              <div className="relative rounded-2xl overflow-hidden h-100] sm:h-112.5 lg:h-125">
                {/* Slider Container */}
                <div className="relative w-full h-full">
                  <div className="absolute inset-0 px-5 bg-linear-to-br from-primary-600/20 to-primary-800 transition-opacity duration-500 opacity-100">
                    <img
                      src={mainProduct?.image || image || undefined}
                      alt={mainProduct?.name || title || "Banner Image"}
                      className={`absolute inset-0 w-full h-full object-cover opacity-80 ${!mainProduct && !image ? "hidden" : ""}`}
                    />
                    {!mainProduct && !image && (
                      <div className="absolute inset-0 w-full h-full bg-neutral-800 animate-pulse" />
                    )}
                    <div className="absolute inset-0 bg-black/30"></div>

                    <div className="relative z-10 h-full flex flex-col justify-center p-6 sm:p-8 lg:p-12">
                      {mainProduct ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <span className="inline-block bg-red-500 text-white text-xs sm:text-sm font-bold px-3 py-1 rounded">
                              Featured
                            </span>
                            <span className="inline-block bg-green-500 text-white text-xs sm:text-sm font-semibold px-3 py-1 rounded uppercase">
                              New Arrival
                            </span>
                          </div>

                          <h2 className="text-white text-xl sm:text-2xl font-semibold">
                            {mainProduct.brand_name}
                          </h2>
                          <h1 className="text-white text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                            {mainProduct.name}
                          </h1>

                          <p className="text-white text-sm sm:text-base max-w-md line-clamp-3">
                            {mainProduct.description}
                          </p>

                          <div className="flex flex-wrap gap-4 pt-4">
                            <Link
                              to={`/product/${mainProduct.id}`}
                              className="inline-block bg-white text-black px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-sm sm:text-base"
                            >
                              Shop Now
                            </Link>
                          </div>
                        </div>
                      ) : (
                        <div className="animate-pulse space-y-4">
                          <div className="h-8 bg-neutral-700/50 rounded w-1/3"></div>
                          <div className="h-12 bg-neutral-700/50 rounded w-2/3"></div>
                          <div className="h-4 bg-neutral-700/50 rounded w-1/2"></div>
                          <div className="h-10 bg-neutral-700/50 rounded w-32 mt-4"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Small Banners Grid - Right Side */}
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                {[sideProduct1, sideProduct2, sideProduct3, sideProduct4].map(
                  (product, idx) =>
                    product ? (
                      <Link
                        key={product.id}
                        to={`/product/${product.id}`}
                        className="relative rounded-xl overflow-hidden h-47.5 sm:h-55 lg:h-59.5 block group cursor-pointer"
                      >
                        <img
                          src={product.image || ""}
                          alt={product.name}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-linear-to-b from-black/60 to-black/30"></div>
                        <div className="relative z-10 p-4 flex flex-col justify-start h-full">
                          <span className="text-white/80 text-xs sm:text-sm font-semibold mb-2">
                            {idx % 2 === 0 ? "New Arrival" : "Trending"}
                          </span>
                          <h4 className="text-white text-sm sm:text-base lg:text-lg font-bold line-clamp-2">
                            {product.name}
                          </h4>
                          <span className="text-primary-300 text-xs sm:text-sm font-semibold mt-auto">
                            Shop Now
                          </span>
                        </div>
                      </Link>
                    ) : (
                      <div
                        key={idx}
                        className="relative rounded-xl overflow-hidden h-47.5 sm:h-55 lg:h-59.5 bg-neutral-800 animate-pulse"
                      >
                        <div className="absolute inset-0 flex flex-col justify-end p-4">
                          <div className="h-4 bg-neutral-700 rounded w-1/3 mb-2"></div>
                          <div className="h-6 bg-neutral-700 rounded w-2/3"></div>
                        </div>
                      </div>
                    ),
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderVariantTwo = () => {
    const product1 = getProduct(0);
    const product2 = getProduct(1);

    return (
      <div className="w-full pt-4 sm:pt-6 md:pt-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <div className="lg:col-span-1">
              {product1 ? (
                <div className={newLocal}>
                  <img
                    src={product1.image || ""}
                    alt={product1.name}
                    className="w-full h-62.5 sm:h-75 md:h-87.5 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30"></div>
                  <div className="absolute inset-0 w-full h-full flex items-center justify-center z-10 p-4">
                    <div className="text-center max-w-xs sm:max-w-sm">
                      <h2 className="text-white font-bold text-xl sm:text-2xl md:text-3xl mb-2 sm:mb-4">
                        {product1.name}
                      </h2>
                      <p className="text-white text-sm sm:text-base md:text-lg mb-4 sm:mb-6 line-clamp-2">
                        {product1.description}
                      </p>
                      <Link
                        to={`/product/${product1.id}`}
                        className="inline-block border border-white text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-white hover:text-primary-600 transition-colors text-sm sm:text-base"
                      >
                        Shop Collection
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative rounded-2xl overflow-hidden min-h-62.5 sm:min-h-75 md:min-h-87.5 bg-neutral-800 animate-pulse">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-8 bg-neutral-700 rounded w-1/2"></div>
                  </div>
                </div>
              )}
            </div>
            <div className="lg:col-span-1">
              {product2 ? (
                <div className="relative rounded-2xl overflow-hidden min-h-62.5 sm:min-h-75 md:min-h-87.5">
                  <img
                    src={product2.image || ""}
                    alt={product2.name}
                    className="w-full h-62.5 sm:h-75 md:h-87.5 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30"></div>
                  <div className="absolute inset-0 w-full h-full flex items-center justify-center z-10 p-4">
                    <div className="text-center max-w-xs sm:max-w-sm">
                      <h2 className="text-white font-bold text-xl sm:text-2xl md:text-3xl mb-2 sm:mb-4">
                        {product2.name}
                      </h2>
                      <p className="text-white text-sm sm:text-base md:text-lg mb-4 sm:mb-6 line-clamp-2">
                        {product2.description}
                      </p>
                      <Link
                        to={`/product/${product2.id}`}
                        className="inline-block border border-white text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-white hover:text-primary-600 transition-colors text-sm sm:text-base"
                      >
                        Shop Now
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative rounded-2xl overflow-hidden min-h-62.5 sm:min-h-75 md:min-h-87.5 bg-neutral-800 animate-pulse">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-8 bg-neutral-700 rounded w-1/2"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderVariantThree = () => {
    const product = getProduct(0);

    return (
      <div className="w-full pt-4 sm:pt-6 md:pt-8 h-full">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="bg-linear-to-r from-primary-600 to-primary-700 rounded-3xl relative overflow-hidden py-8 sm:py-12 md:py-16 h-full flex items-center">
            {product ? (
              <div className="flex flex-col lg:flex-row items-center w-full">
                <div className="lg:w-1/2 px-4 sm:px-8 lg:pl-16 text-center lg:text-left">
                  <div className="text-white">
                    <h1 className="font-bold text-2xl sm:text-3xl md:text-4xl mb-4 sm:mb-6 leading-tight">
                      {product.name}
                    </h1>
                    <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 leading-relaxed line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
                      <Link
                        to={`/product/${product.id}`}
                        className="bg-white text-primary-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-medium hover:bg-primary-50 hover:text-primary-700 border border-white transition-colors text-sm sm:text-base"
                      >
                        Shop Now
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="lg:w-1/2 mt-6 sm:mt-8 lg:mt-0 px-4 sm:px-8">
                  <div className="text-center lg:text-right">
                    <img
                      src={product.image || ""}
                      alt={product.name}
                      className="max-w-full h-auto rounded-lg mx-auto lg:ml-auto"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center animate-pulse">
                <div className="h-8 bg-white/20 rounded w-1/3"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (variant) {
      case "two":
        return renderVariantTwo();
      case "three":
        return renderVariantThree();
      default:
        return renderEnhancedHero();
    }
  };

  return <section className={`w-full ${className}`}>{renderContent()}</section>;
};

export default Banner;
// function updateDots(parentElement: HTMLElement | null, activeIndex: number) {
//   if (!parentElement) return;
//   const dots = parentElement.querySelectorAll("[data-dot]");
//   dots.forEach((dot, idx) => {
//     if (idx === activeIndex) {
//       dot.classList.remove("bg-white/50");
//       dot.classList.add("bg-white");
//     } else {
//       dot.classList.remove("bg-white");
//       dot.classList.add("bg-white/50");
//     }
//   });
// }

// function goToSlide(
//   e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
//   targetIndex: number,
// ): void {
//   const parent: HTMLElement | null = e.currentTarget.closest(".relative");
//   if (!parent) return;
//   const slides = parent.querySelectorAll("[data-slide]");
//   slides.forEach((slide, idx) => {
//     if (idx === targetIndex) {
//       slide.classList.replace("opacity-0", "opacity-100");
//     } else {
//       slide.classList.replace("opacity-100", "opacity-0");
//     }
//   });
//   updateDots(parent, targetIndex);
// }
